import React, {useEffect, useRef, useState} from 'react';
import {Header} from "./components/Header";
import {faChevronRight, faPlus, faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ButtonGroupSelect, {ButtonSelectOption} from "./components/ButtonGroupSelect";
import ImageGroupSelect from "./components/ImageGroupSelect";
import InputItem from "./components/InputItem";
import {Alert, Button, Form} from 'react-bootstrap';
import {db} from "./firebase";
import RangeSlider from 'react-bootstrap-range-slider';
import {CalculatedOption, ExtraItem, Item, KoluSet, Room, Values} from "./config/interfaces";
import {Brand, CableTrayType, HouseType, PlacementOutside, RoomType, WallType} from "./config/enums";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {cableTrayOptions, floorTypes, houseTypesOptions, roomTypes, wallTypeOptions} from "./config/options";
import {
    aerialPlatform15,
    aluminiumTray, DaikinEmuraGrijsInsideUnit,
    DaikinEmuraWitInsideUnit,
    DaikinStylishGrijsInsideUnit,
    DaikinStylishWitInsideUnit,
    DaikinStylishZwartHoutInsideUnit,
    designTray,
    highFacadeExtraCost,
    LGArtcoolZwartInsideUnit,
    LGDeluxeInsideUnit,
    roofExtraCost
} from "./config/extra-items";
import Viewer from "./components/Viewer";
import CostOverview from "./components/CostOverview";
import TotalOverviewDialog from "./components/TotalOverviewDialog";
import {cloneDeep} from 'lodash';
import { NotificationManager } from 'react-notifications';
import TagManager from "react-gtm-module";
import FinalModal from "./components/FinalModal";

const defaultRoom: Room = {surface: 12, type: RoomType.bedroom, floor: 0, cableTrayType: CableTrayType.standard, unitDesign: 'default'};

function App() {
    const [brand, setBrand] = useState<Brand | 'auto'>('auto');
    const [houseType, setHouseType] = useState<HouseType>(HouseType.open);
    const [constructionYear, setConstructionYear] = useState<number>(1975);
    const [wallType, setWallType] = useState<WallType>(WallType.concrete);
    const [currentItem, setCurrentItem] = useState<string>('airco-designer');
    const [rooms, setRooms] = useState<Room[]>([{...defaultRoom}]);
    const [roomsSurfaces, setRoomsSurfaces] = useState<number[]>([12]);
    const [aircoItems, setAircoItems] = useState<Item[]>([]);
    const [calculatedOptions, setCalculatedOptions] = useState<CalculatedOption[]>([]);
    const [showTotalDialog, setShowTotalDialog] = useState<boolean>(false);
    const [calculatedToggle, setCalculatedToggle] = useState<boolean>(true);
    const [showFinalModal, setShowFinalModal] = useState<boolean>(false);

    const roomsRef = useRef([] as HTMLDivElement[]);

    useEffect(() => {
        document.title = 'Kolu Calculator';
        db.collection('airco_items').get().then(result => {
            setAircoItems(result.docs.map(doc => Object.assign({}, (doc.data() as Item), {merk: Brand[doc.data().merk as keyof typeof Brand]})).filter(doc => {
                const testId = doc.id.toUpperCase();
                // Removal of floor models because they have specific characteristics that can not be handled with this calculator yet.
                // Removal of Comfora Daikin airco because they only have the option with single split and this is not handled yet.
                // Removal of Air Purification airco because it's special and not yet implemented.
                return !testId.endsWith('-VL') && !testId.endsWith('-VLOERMODEL') && !testId.endsWith('-AP');
            }));
        });

        // Initialize tag manager
        const tagManagerArgs = {
            gtmId: "GTM-TDR4LLB",
        };
        TagManager.initialize(tagManagerArgs);
    }, []);

    const calculateTotalPrice = (combi: CalculatedOption): CalculatedOption => {
        const btw = (new Date()).getFullYear() - constructionYear > 10 ? 6 : 21;
        let totalPriceExcl: number = 0, totalPriceIncl: number, extraItems: ExtraItem[] = [], addAerialPlatform = false;
        combi.sets.forEach(set => {
            if (set.item !== null) {
                totalPriceExcl += set.item!.prijs;
                if (set.placementOutside === PlacementOutside.facadeHigh) {
                    addAerialPlatform = true;
                    extraItems.push(highFacadeExtraCost);
                } else if (set.placementOutside === PlacementOutside.roof) {
                    extraItems.push(roofExtraCost);
                }

                set.rooms.forEach(room => {
                    if (room.cableTrayType === CableTrayType.design)
                        (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(designTray));
                    if (room.cableTrayType === CableTrayType.aluminium)
                        (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(aluminiumTray));

                    if (set.item!.aantalKamers! > 1) {
                        switch (room.unitDesign) {
                            case 'stylish-zwarthout':
                                (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(DaikinStylishZwartHoutInsideUnit));
                                break;
                            case 'stylish-silver':
                                (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(DaikinStylishGrijsInsideUnit));
                                break;
                            case 'stylish-white':
                                (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(DaikinStylishWitInsideUnit));
                                break;
                            case 'emura-wit':
                                (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(DaikinEmuraWitInsideUnit));
                                break;
                            case 'emura-silver':
                                (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(DaikinEmuraGrijsInsideUnit));
                                break;
                            case 'deluxe':
                                (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(LGDeluxeInsideUnit));
                                break;
                            case 'artcool':
                            case 'artcool-silver':
                                (new Array(room.numberOfUnits)).fill(0).forEach(() => extraItems.push(LGArtcoolZwartInsideUnit));
                                break;
                        }
                    } else {
                        let code;
                        let defaultCode = set.item!.merk === 'LG' ? 'SP' : 'PE';
                        let idFirstPart = set.item!.id.split('-')[0];
                        switch (room.unitDesign) {
                            case 'default':
                                code = defaultCode;
                                break;
                            case 'comfora':
                                code = 'CO';
                                break;
                            case 'stylish-zwarthout':
                            case 'stylish-silver':
                                code = 'SK';
                                break;
                            case 'stylish-white':
                                code = 'SW';
                                break;
                            case 'emura-wit':
                                code = 'EW';
                                break;
                            case 'emura-silver':
                                code = 'EZ';
                                break;
                            case 'deluxe':
                                code = 'DE';
                                break;
                            case 'artcool':
                            case 'artcool-silver':
                                code = 'AZ';
                                break;
                        }
                        const newIdToTest = idFirstPart + '-' + code;
                        if (newIdToTest !== set.item!.id) {
                            let item = aircoItems.filter(ai => ai.id === newIdToTest)[0];
                            if (!item) {
                                NotificationManager.info('Het geselecteerde design is niet beschikbaar in deze configuratie.');
                                const defaultId = idFirstPart + '-' + defaultCode;
                                item = aircoItems.filter(ai => ai.id === defaultId)[0];
                                set.rooms[0].unitDesign = 'default'; // Reset to default because selected option is not available
                            }
                            if (!item) {
                                NotificationManager.error('Er is een onmogelijke fout opgetreden...');
                            } else {
                                set.item = item;
                            }
                        }
                    }
                });
            }
        });
        if (addAerialPlatform) extraItems.push(aerialPlatform15);
        totalPriceExcl += extraItems.reduce((acc, curr) => acc + curr.price, 0);
        /*switch (houseType) {
            case HouseType.halfopen:
                totalPriceExcl *= 1.01;
                break;
            case HouseType.closed:
                totalPriceExcl *= 1.02;
                break;
            case HouseType.apartment:
                totalPriceExcl *= 1.03;
                break;
            default:
                break;
        }
        switch (wallType) {
            case WallType.concrete:
                totalPriceExcl *= 1.02;
                break;
            case WallType.crepi:
            case WallType.woodenSkeleton:
                totalPriceExcl *= 1.01;
                break;
            default:
                break;
        }
        if (constructionYear < 1950) {
            totalPriceExcl *= 1.02;
        } else if (constructionYear < 2000) {
            totalPriceExcl *= 1.01;
        }*/

        totalPriceIncl = totalPriceExcl * btw / 100 + totalPriceExcl;

        totalPriceExcl = Math.round(totalPriceExcl * 100) / 100;
        totalPriceIncl = Math.round(totalPriceIncl * 100) / 100;

        return Object.assign({}, combi, {totalPriceIncl, totalPriceExcl, extraItems});
    }

    const checkCalculatedOptionsString = JSON.stringify(calculatedOptions.map(o => o.sets));

    useEffect(() => {
        // Recalculate when sets are changed
        const calculatedOptionsCopy = cloneDeep(calculatedOptions);
        setCalculatedOptions(calculatedOptionsCopy.map(co => calculateTotalPrice(co)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkCalculatedOptionsString]);

    const removeRoom = (index: number) => {
        setRooms(rooms.filter((d, i) => i !== index));
        setRoomsSurfaces(roomsSurfaces.filter((d, i) => i !== index));
    }

    const addRoom = () => {
        setRooms([...rooms, {...defaultRoom}]);
        setRoomsSurfaces([...roomsSurfaces, 12]);
    }

    useEffect(() => {
        const copy = [...rooms];
        let changed = false;
        copy.map(room => {
            if (room.type === RoomType.cellar && room.floor !== -1) {
                changed = true;
                room.floor = -1;
            }
            return room;
        });

        if (changed) setRooms(copy);
    }, [rooms]);

    const mapCombinationToSets = (combination: CalculatedOption, values: Values): KoluSet[] | false => {
        // We add the original indexes of the rooms to the array to know the place where to remove after sorting and selecting. Also adding the needed number of units for that room.
        // let roomsCopy = [...rooms.map((p, i) => ({...p, i, numberOfUnits: Math.ceil(p.surface / values.largestAreaWithOneInsideUnit)}))].sort((a, b) => b.surface - a.surface);
        let roomsCopy = [...values.rooms];
        const sets: KoluSet[] = [];
        combination.items.sort((a, b) => a.opp === b.opp ? a.prijs - b.prijs : b.opp - a.opp).forEach((item) => {
            const newSet: KoluSet = {rooms: [], item, placementOutside: PlacementOutside.ground};
            let leftSpace = item.opp - newSet.rooms.reduce((acc, curr) => acc + curr.surface, 0);
            let leftUnits = item.aantalKamers - newSet.rooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0);

            // Functions should be outside, otherwise created at each alteration.
            const filter = (r: Room) => r.surface <= leftSpace && r.numberOfUnits! <= leftUnits;
            const reduce = (min: number, room: Room) => room.numberOfUnits! <= leftUnits ? Math.min(room.surface, min) : min;

            while (leftUnits > 0 && leftSpace > roomsCopy.reduce(reduce, 1000)) {
                const bestAdded = roomsCopy.filter(filter)[0];
                // console.log('bestAdded', bestAdded);
                if (bestAdded) {
                    newSet.rooms.push(bestAdded);
                    roomsCopy = roomsCopy.filter((r: Room) => r.i !== bestAdded.i);
                } else {
                    break;
                }
                leftSpace = item.opp - newSet.rooms.reduce((acc, curr) => acc + curr.surface, 0);
                leftUnits = item.aantalKamers - newSet.rooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0);
            }
            sets.push(newSet);
        });
        // Checking the number of rooms should not be needed as normally it'd not be possible as the last child is filtered on number of rooms precisely.
        return roomsCopy.length === 0 ? sets : false;
    }

    const _checkPossibilities = (possibleItems: Item[], numberOfSets: number, values: Values, current: Item[] = []) => {
        let possibilities: CalculatedOption[] = [];
        if (current.length === 0) {
            // Only use the default inside unit designs for single split
            possibleItems = possibleItems.filter(item => item.aantalKamers > 1 || (item.model.endsWith('PE') || item.model.endsWith('SP')));
        }
        (current.length > 0 ? possibleItems.filter(i => i.merk === current[0].merk && (current.length === numberOfSets - 1 ? i.aantalKamers === values.neededUnits - current.reduce((acc, curr) => acc + curr.aantalKamers, 0) : true)) : possibleItems).forEach((item) => {
            const {neededUnits, neededSurface, largestArea, maxInsideUnitsPerOutsideUnit} = values;

            const newCurrent = [...current, {...item}];
            const totalSurface = newCurrent.reduce((acc, curr) => acc + curr.opp, 0);
            const totalUnits = newCurrent.reduce((acc, curr) => acc + curr.aantalKamers, 0);

            if (totalSurface + ((numberOfSets - newCurrent.length) * largestArea) < neededSurface || totalUnits + ((numberOfSets - newCurrent.length) * maxInsideUnitsPerOutsideUnit) < neededUnits) {
                // Is this is true, give up. This gives no possibilities.
                return [];
            }
            if (newCurrent.length === numberOfSets) {
                // This should be the last element of the combination
                if (totalSurface >= neededSurface && totalUnits >= neededUnits) {
                    // if totalSurface is greater then neededSurface the system has enough power to use all the units
                    // totalUnits should be greater then neededUnits
                    const option: CalculatedOption = {
                        totalPrice: newCurrent.reduce((acc, curr) => acc + curr.prijs, 0),
                        brand: newCurrent[0].merk,
                        items: newCurrent,
                        sets: [],
                        calculatedSets: []
                    };
                    const sets = mapCombinationToSets(option, values);
                    if (sets) {
                        possibilities.push({...option, sets});
                    }
                }
            } else {
                // Not the last element, just go on recursive if it's no lost cause
                // Object.assign(possibilities, _checkPossibilities(possibleItems, numberOfSets, values, newCurrent));
                possibilities = [...possibilities, ..._checkPossibilities(possibleItems, numberOfSets, values, newCurrent)];
            }
        });

        // resolve(possibilities);
        return possibilities;
    }

    const smoothBestOptions = (bestOptions: CalculatedOption[], options: CalculatedOption[]): CalculatedOption[] => {
        if (bestOptions.length === 1) return bestOptions;
        const testValues = bestOptions.map(o => o.sets.map(s => s.item!.aantalKamers).sort()).reduce((prev, curr) => prev.map(arr => arr.join('|')).includes(curr.join('|')) ? [...prev] : [...prev, curr], [] as number[][]);
        if (testValues.length === 1) return bestOptions;
        // vanaf hier testen wat de goedkoopste optie zou zijn...
        const possibilities = [] as CalculatedOption[][];
        testValues.forEach((value) => {
            const newOption = [] as CalculatedOption[];
            bestOptions.forEach((bestOption) => {
                if (bestOption.sets.map(o => o.item!.aantalKamers).sort().join('|') === value.join('|')) {
                    newOption.push(bestOption);
                } else {
                    newOption.push(options.filter(opt => opt.sets.map(s => s.item!.aantalKamers).sort().join('|') === value.join('|') && opt.brand === bestOption.brand).sort((a, b) => a.totalPriceIncl! - b.totalPriceIncl!)[0]);
                }
            });
            possibilities.push(newOption);
        });
        return possibilities.sort((a, b) => a.reduce((prev, curr) => prev + curr.totalPriceIncl!, 0) - b.reduce((prev, curr) => prev + curr.totalPriceIncl!, 0))[0];
    }

    const checkPossibilities = async (possibleItems: Item[], numberOfSets: number, values: Values): Promise<Array<CalculatedOption>> => {
        return new Promise((resolve, reject) => {
            let options = [];
            do {
                options = _checkPossibilities(possibleItems, numberOfSets, values).map(p => {
                    p.totalPrice = Math.round(p.totalPrice * 100) / 100;
                    return p;
                });
                numberOfSets++;
            } while (options.length === 0);
            resolve(options);
        });
    }

    useEffect(() => {
        if (aircoItems.length > 0) {
            const possibleItems = brand === 'auto' ? [...aircoItems] : [...aircoItems.filter(item => item.merk === Brand[brand as keyof typeof Brand])];
            const totalSurface = rooms.reduce((acc, curr) => acc + curr.surface, 0);

            const largestAreaWithOneInsideUnit = possibleItems.filter(i => i.aantalKamers === 1).sort((a, b) => a.opp === b.opp ? a.prijs - b.prijs : b.opp - a.opp)[0].opp;
            const largestArea = possibleItems.sort((a, b) => a.opp === b.opp ? a.prijs - b.prijs : b.opp - a.opp)[0].opp;
            const neededUnits = rooms.reduce((acc, room) => acc + Math.ceil(room.surface / largestAreaWithOneInsideUnit), 0); // If we have a very big room multiple units are needed

            const neededSurface = rooms.reduce((acc, curr) => acc + curr.surface, 0);
            let oneUnitPossibilities = possibleItems.filter(item => item.opp >= totalSurface && item.aantalKamers === neededUnits);
            oneUnitPossibilities = oneUnitPossibilities.sort((a, b) => a.opp === b.opp ? a.prijs - b.prijs : a.opp - b.opp);
            let bestOptions: CalculatedOption[] = [];
            if (oneUnitPossibilities.length > 0) {
                const roomsCopy: Room[] = [...rooms.map((p, i) => ({
                    ...p,
                    i,
                    numberOfUnits: Math.ceil(p.surface / largestAreaWithOneInsideUnit)
                }))];
                if (brand === 'auto' || Brand[brand as keyof typeof Brand] === Brand.DA) {
                    const selected = oneUnitPossibilities.filter(u => u.merk === Brand.DA)[0];
                    if (selected) {
                        bestOptions.push(calculateTotalPrice({
                            totalPrice: selected.prijs,
                            brand: selected.merk,
                            items: [selected as Item],
                            sets: [{item: selected as Item, rooms: [...roomsCopy], placementOutside: PlacementOutside.ground}],
                            calculatedSets: [{item: selected as Item, rooms: [...roomsCopy], placementOutside: PlacementOutside.ground}]
                        }));
                    }
                }
                if (brand === 'auto' || Brand[brand as keyof typeof Brand] === Brand.LG) {
                    const selected = oneUnitPossibilities.filter(u => u.merk === Brand.LG)[0];
                    if (selected) {
                        bestOptions.push(calculateTotalPrice({
                            totalPrice: selected.prijs,
                            brand: selected.merk,
                            items: [selected as Item],
                            sets: [{item: selected as Item, rooms: [...roomsCopy], placementOutside: PlacementOutside.ground}],
                            calculatedSets: [{item: selected as Item, rooms: [...roomsCopy], placementOutside: PlacementOutside.ground}]
                        }));
                    }
                }
                setCalculatedOptions(bestOptions);
                setCalculatedToggle(!calculatedToggle);
            } else {
                const maxInsideUnitsPerOutsideUnit = 5;
                // Try to find combinations
                let minNumberOfSets = Math.max(Math.ceil(neededUnits / maxInsideUnitsPerOutsideUnit), Math.ceil(neededSurface / largestArea));

                const maxMultiDA = possibleItems.filter(i => i.merk === Brand.DA).sort((a, b) => b.opp - a.opp)[0]?.opp;
                const maxMultiLG = possibleItems.filter(i => i.merk === Brand.LG).sort((a, b) => b.opp - a.opp)[0]?.opp;

                const defaultSetsDA: KoluSet[] = [];
                const defaultSetsLG: KoluSet[] = [];

                const roomsCopy: Room[] = [...rooms.map((p, i) => ({
                    ...p,
                    i,
                    numberOfUnits: Math.ceil(p.surface / largestAreaWithOneInsideUnit)
                })).sort((a, b) => b.surface - a.surface)];

                while (minNumberOfSets > 4) {
                    const newSetDA: KoluSet = {item: null, rooms: [], placementOutside: PlacementOutside.ground};
                    const newSetLG: KoluSet = {item: null, rooms: [], placementOutside: PlacementOutside.ground};
                    let maxToAdd = Math.min(maxMultiDA, maxMultiLG);

                    const filter = (d: Room) => d.surface <= maxToAdd && d.numberOfUnits! < maxInsideUnitsPerOutsideUnit - newSetDA.rooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0);
                    let best = roomsCopy.filter(filter)[0];
                    while (best && newSetDA.rooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0) < maxInsideUnitsPerOutsideUnit) {
                        const room = roomsCopy.splice(roomsCopy.indexOf(best), 1)[0];
                        newSetDA.rooms.push(room);
                        newSetLG.rooms.push(room);
                        maxToAdd = Math.min(maxMultiDA - newSetDA.rooms.reduce((acc, curr) => acc + curr.surface, 0), maxMultiLG - newSetDA.rooms.reduce((acc, curr) => acc + curr.surface, 0));
                        best = roomsCopy.filter(filter)[0];
                    }
                    newSetDA.item = possibleItems.filter(i => i.opp === maxMultiDA && i.aantalKamers === newSetDA.rooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0))[0]
                    newSetLG.item = possibleItems.filter(i => i.opp === maxMultiLG && i.aantalKamers === newSetLG.rooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0))[0]
                    defaultSetsDA.push(newSetDA);
                    defaultSetsLG.push(newSetLG);
                    minNumberOfSets = Math.max(Math.ceil((roomsCopy.reduce((acc, room) => acc + room.numberOfUnits!, 0)) / maxInsideUnitsPerOutsideUnit), Math.ceil((roomsCopy.reduce((acc, curr) => acc + curr.surface, 0)) / largestArea));
                }

                const values = {
                    largestArea,
                    neededUnits: roomsCopy.reduce((acc: number, room: Room) => acc + room.numberOfUnits!, 0),
                    neededSurface: roomsCopy.reduce((acc: number, room: Room) => acc + room.surface, 0),
                    maxInsideUnitsPerOutsideUnit,
                    rooms: roomsCopy,
                }

                checkPossibilities(possibleItems, minNumberOfSets, values).then(
                    (options) => {
                        options.sort((a, b) => a.totalPrice - b.totalPrice);
                        const selectedDA = options.filter(o => o.brand === Brand.DA)[0];
                        const selectedLG = options.filter(o => o.brand === Brand.LG)[0];
                        if (brand === 'auto' || Brand[brand as keyof typeof Brand] === Brand.DA) {
                            bestOptions.push(
                                calculateTotalPrice({
                                    brand: Brand.DA,
                                    items: [...selectedDA.items, ...defaultSetsDA.reduce<Item[]>((acc, curr) => [...acc, curr.item!], [])],
                                    sets: [...selectedDA.sets, ...defaultSetsDA.reduce<KoluSet[]>((acc, curr) => [...acc, curr], [])],
                                    calculatedSets: [...selectedDA.sets, ...defaultSetsDA.reduce<KoluSet[]>((acc, curr) => [...acc, curr], [])]
                                } as CalculatedOption)
                            );
                        }
                        if (brand === 'auto' || Brand[brand as keyof typeof Brand] === Brand.LG) {
                            bestOptions.push(
                                calculateTotalPrice({
                                    brand: Brand.LG,
                                    items: [...selectedLG.items, ...defaultSetsLG.reduce<Item[]>((acc, curr) => [...acc, curr.item!], [])],
                                    sets: [...selectedLG.sets, ...defaultSetsLG.reduce<KoluSet[]>((acc, curr) => [...acc, curr], [])],
                                    calculatedSets: [...selectedLG.sets, ...defaultSetsLG.reduce<KoluSet[]>((acc, curr) => [...acc, curr], [])]
                                } as CalculatedOption)
                            );
                        }
                        setCalculatedOptions(smoothBestOptions(bestOptions, options));
                        setCalculatedToggle(!calculatedToggle);
                    }
                );

            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rooms, brand, houseType, constructionYear, wallType, aircoItems]);

  return <>
        <Header />
        <Viewer wallType={wallType} constructionYear={constructionYear} houseType={houseType} addRoom={addRoom} currentItem={currentItem} roomsRef={roomsRef} removeRoom={removeRoom} rooms={rooms} roomsSurfaces={roomsSurfaces} />
        <div id={'input'}>
            <InputItem itemKey={'airco-designer'} currentItemKey={currentItem} changeItemKey={(key: string) => setCurrentItem(key)}>
                <h1>Airco Designer</h1>
                <p className={'text-muted'}>Verwachtte levertijd: 4-7 weken</p>
                <ButtonGroupSelect value={brand} onChange={(brand: Brand) => setBrand(brand)} options={[{key: 'auto', name: 'Automatisch'}, ...Object.keys(Brand).map((key) => ({key: key, name: Brand[key as keyof typeof Brand]}))] as ButtonSelectOption[]} />
                <Alert variant={'success'} className={'font-weight-bold mt-5'}>Vraag onmiddellijk je offerte aan en geniet van &euro;100 korting op je berekende prijs!</Alert>
            </InputItem>
            <InputItem itemKey={'house-type'} currentItemKey={currentItem} changeItemKey={(key: string) => setCurrentItem(key)}>
                <h3>Type woning</h3>
                <p className={'text-muted'}>In wat voor woning woon je?</p>
                <ImageGroupSelect options={houseTypesOptions} value={houseType} onChange={(key: HouseType) => setHouseType(key)} />
            </InputItem>
            <InputItem itemKey={'construction-year'} currentItemKey={currentItem} changeItemKey={(key: string) => setCurrentItem(key)}>
                <h3>Bouwjaar ({constructionYear})</h3>
                <p className={'text-muted'}>Ouder dan 10 jaar? Dan geniet u van slechts 6% btw.</p>
                <Form.Group>
                    <RangeSlider size={'lg'} value={constructionYear} min={1920} max={(new Date()).getFullYear()} onChange={(event) => {setConstructionYear(parseInt((event.target as HTMLInputElement).value));}} className={'custom-range'} />
                </Form.Group>
            </InputItem>
            <InputItem itemKey={'wall-type'} currentItemKey={currentItem} changeItemKey={(key: string) => setCurrentItem(key)}>
                <h3>Type muur</h3>
                <p className={'text-muted'}>Selecteer het type muur van je woning.</p>
                <ImageGroupSelect options={wallTypeOptions} value={wallType} onChange={(key: WallType) => setWallType(key)} />
            </InputItem>
            {rooms.map((_, i) => {
                return <InputItem key={'room:' + i} setRef={(element: HTMLDivElement) => roomsRef.current[i] = element} itemKey={'room:' + i} currentItemKey={currentItem} changeItemKey={(key: string) => setCurrentItem(key)}>
                    <h3>Kamer #{i + 1}</h3>
                    <Form.Group>
                        <h5>Oppervlakte ({roomsSurfaces[i]} m²)</h5>
                        <RangeSlider size={'lg'} value={roomsSurfaces[i]} tooltipLabel={currentValue => `${currentValue} m²`} min={10} max={120} onChange={(event) => {const surfaces = [...roomsSurfaces];surfaces[i] = parseInt(event.target.value);setRoomsSurfaces(surfaces);}} onAfterChange={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {const roomsCopy = [...rooms];roomsCopy[i].surface = roomsCopy[i].type === RoomType.attic ? parseInt((event.target as HTMLInputElement).value) * 1.1 : parseInt((event.target as HTMLInputElement).value);setRooms(roomsCopy);}} className={'custom-range'} />
                    </Form.Group>
                    <Form.Group className={'mt-5'}>
                        <h5>Type kamer</h5>
                        <ImageGroupSelect options={roomTypes} value={_.type} onChange={(value: RoomType) => {const roomsCopy = [...rooms];roomsCopy[i].surface = (roomsCopy[i].type !== RoomType.attic && value === RoomType.attic ? roomsCopy[i].surface * 1.1 : (roomsCopy[i].type === RoomType.attic && value !== RoomType.attic ? roomsCopy[i].surface / 1.1 : roomsCopy[i].surface));roomsCopy[i].type = value;setRooms(roomsCopy);}} />
                    </Form.Group>
                    <Form.Group className={'mt-5'}>
                        <h5>Verdieping</h5>
                        {_.type === RoomType.cellar ?
                            <Button variant={'outline-main'} disabled active>Kelderverdieping</Button>
                        :
                            <ButtonGroupSelect vertical options={floorTypes} value={_.floor} onChange={(value: number) => {const roomsCopy = [...rooms];roomsCopy[i].floor = value;setRooms(roomsCopy);}} />
                        }
                    </Form.Group>
{/*                    <Form.Group className={'mt-5'}>
                        <h3>Type kabelgoot</h3>
                        <ImageGroupSelect options={cableTrayOptions} value={_.cableTrayType} onChange={(value: CableTrayType) => {const roomsCopy = [...rooms];roomsCopy[i].cableTrayType = value;setRooms(roomsCopy);}} />
                    </Form.Group>*/}
                    {rooms.length > 1 && <Button variant={"outline-danger"} className={'mt-5'} onClick={() => removeRoom(i)}><FontAwesomeIcon icon={faTimes} /> Verwijder kamer {i + 1}</Button>}
                </InputItem>
            })}
            <InputItem itemKey={'room'} currentItemKey={currentItem} changeItemKey={(key: string) => setCurrentItem(key)}>
                <Button variant={'main'} onClick={addRoom}><FontAwesomeIcon icon={faPlus} /> Kamer toevoegen</Button>
            </InputItem>
            <InputItem itemKey={'room'} currentItemKey={currentItem} changeItemKey={(key: string) => setCurrentItem(key)}>
                <Alert variant={'success'} className={'mb-5'}><span className={'font-weight-bold'}>Je bent er bijna!</span> Finaliseer je offerte en ontvang je &euro;100 korting.</Alert>
                <Button className={'overviewButton'} variant={'success'} size={'lg'} onClick={() => setShowTotalDialog(true)}>Verder <FontAwesomeIcon icon={faChevronRight} /></Button>
            </InputItem>
        </div>
      <CostOverview options={calculatedOptions} onClick={() => setShowTotalDialog(true)} />
      {calculatedOptions.length > 0 && <TotalOverviewDialog setShowFinalModal={setShowFinalModal} aircoItems={aircoItems} close={() => setShowTotalDialog(false)} show={showTotalDialog} calculatedOptions={calculatedOptions} setCalculatedOptions={setCalculatedOptions} items={aircoItems} calculatedToggle={calculatedToggle} constructionYear={constructionYear} houseType={houseType} wallType={wallType}/>}
      {showFinalModal && <FinalModal />}
    </>;
}

export default App;
